import React, { useState, useRef, useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { useScreenSize } from '@/hooks';
import { fetchGisGames, fetchGisGamesByType, fetchPragmaticSlots } from '@/services/api';
import { useGames } from '@/Context/GameContext';
import styles from './GameCards.module.css';

/**
 * GameCards Component (Reusable)
 * Displays games from multiple providers with carousel and grid views
 * Handles image errors properly - filters out games with broken images
 */

/**
 * GameCards Component (Reusable)
 * Displays games from multiple providers with carousel and grid views
 * @param {Object} props
 * @param {string[]} props.providers - Array of provider names to fetch games from (for GIS API)
 * @param {string} props.title - Section title (default: 'Live Casino')
 * @param {number} props.itemsPerPage - Number of items per page (default: 20)
 * @param {Function} props.fetchFunction - Custom fetch function (for JS Games API like slots)
 * @param {Function} props.launchFunction - Custom launch function (default: launchGisGame)
 * @param {string} props.queryKey - Custom query key for React Query
 */
const GameCards = ({
  providers = null,
  gameTypes = null,
  title = 'Live Casino',
  itemsPerPage = 20,
  fetchFunction = null,
  launchFunction = null,
  queryKey = null
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleGameLaunch } = useGames();
  const { isMobile } = useScreenSize();
  const scrollContainerRef = useRef(null);
  const [showMore, setShowMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [failedImages, setFailedImages] = useState(new Set());

  // Determine fetch mode
  const useCustomFetch = fetchFunction !== null;
  const useCustomLaunch = launchFunction !== null;
  const useTypeFetch = !useCustomFetch && gameTypes && gameTypes.length > 0;

  // Fetch games using custom function (for JS Games like slots)
  const customQuery = useQuery({
    queryKey: queryKey || ['customGames', currentPage, isMobile],
    queryFn: () => fetchFunction(currentPage, itemsPerPage, isMobile),
    enabled: useCustomFetch,
    staleTime: 300000,
    retry: 2,
  });

  // Fetch games by game type (e.g. 'crash', 'live dealer', 'slots')
  const typeQueries = useQueries({
    queries: useTypeFetch
      ? (gameTypes || []).map((type) => ({
          queryKey: ['typeGames', type, isMobile],
          queryFn: () => fetchGisGamesByType(type, isMobile),
          staleTime: 300000,
          retry: 2,
        }))
      : [],
  });

  // Fetch games from all providers using useQueries (for GIS)
  const useProviderFetch = !useCustomFetch && !useTypeFetch && providers && providers.length > 0;
  const providerQueries = useQueries({
    queries: useProviderFetch
      ? providers.map((provider) => ({
          queryKey: ['providerGames', provider, currentPage, isMobile],
          queryFn: () => fetchGisGames(provider, currentPage, itemsPerPage, '', isMobile),
          staleTime: 300000,
          retry: 2,
        }))
      : [],
  });

  // Combine all games from different sources
  const { games: allGames, pagination, isLoading, error } = useMemo(() => {
    if (useCustomFetch) {
      const customGames = customQuery.data || [];
      return {
        games: customGames,
        pagination: {
          current_page: currentPage,
          total_pages: customGames.length >= itemsPerPage ? currentPage + 1 : currentPage,
        },
        isLoading: customQuery.isLoading,
        error: customQuery.error,
      };
    }

    if (useTypeFetch) {
      // Using game type API
      const merged = [];
      let loading = false;
      let hasError = false;
      typeQueries.forEach((query) => {
        if (query.data?.games) {
          query.data.games.forEach((game) => {
            if (!merged.find((g) => g.uuid === game.uuid)) merged.push(game);
          });
        }
        if (query.isLoading) loading = true;
        if (query.error) hasError = true;
      });
      return {
        games: merged,
        pagination: { current_page: 1, total_pages: 1 },
        isLoading: loading,
        error: hasError,
      };
    }

    // Using providers (GIS API)
    const allGames = [];
    const allPagination = [];
    let loading = false;
    let hasError = false;
    providerQueries.forEach((query) => {
      if (query.data?.games) {
        query.data.games.forEach((game) => {
          if (!allGames.find((g) => g.uuid === game.uuid)) allGames.push(game);
        });
        if (query.data.pagination) allPagination.push(query.data.pagination);
      }
      if (query.isLoading) loading = true;
      if (query.error) hasError = true;
    });
    const maxTotalPages = allPagination.length > 0
      ? Math.max(...allPagination.map((p) => p.total_pages || 1))
      : 1;
    return {
      games: allGames,
      pagination: { current_page: currentPage, total_pages: maxTotalPages },
      isLoading: loading,
      error: hasError,
    };
  }, [useCustomFetch, useTypeFetch, customQuery, typeQueries, providerQueries, currentPage, itemsPerPage]);

  // Debug: log full game objects by provider
  useMemo(() => {
    if (allGames.length > 0) {
      const byProvider = {};
      allGames.forEach((game) => {
        const provider = game.provider || game.vendor || 'unknown';
        if (!byProvider[provider]) byProvider[provider] = [];
        byProvider[provider].push(game);
      });
      console.log(`[GameCards: ${title}] Total games: ${allGames.length}`, JSON.parse(JSON.stringify(byProvider)));
    }
  }, [allGames, title]);

  // Filter out games with failed images or invalid image URLs
  const games = useMemo(() => {
    return allGames.filter((game) => {
      // Skip items where image has failed to load
      const gameId = game.id || game.uuid || game.game_uid;
      if (failedImages.has(gameId)) {
        return false;
      }
      
      // Skip items with invalid or missing image URLs
      const gameIcon = game.game_icon || game.image || '';
      if (!gameIcon || 
          gameIcon === null ||
          gameIcon === '/assets/images/default_card3.webp' ||
          gameIcon === '/assets/images/placeholder.webp' ||
          gameIcon.trim() === '' ||
          gameIcon === 'undefined' ||
          gameIcon === 'null') {
        return false;
      }
      
      // Validate image URL format
      const validImageUrl = gameIcon && 
                           (gameIcon.startsWith('http') || gameIcon.startsWith('/')) &&
                           !gameIcon.includes('undefined') &&
                           !gameIcon.includes('null');
      
      return validImageUrl;
    });
  }, [allGames, failedImages]);

  // Handle image load errors
  const handleImageError = (gameId, event) => {
    // Prevent infinite loop by checking if we've already marked this image as failed
    if (!failedImages.has(gameId)) {
      setFailedImages((prev) => new Set([...prev, gameId]));
      // Set placeholder to prevent broken image icon
      if (event.target) {
        event.target.src = '/assets/images/default_card3.webp';
        // Hide the image if placeholder also fails
        event.target.onerror = () => {
          event.target.style.display = 'none';
        };
      }
    }
  };

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      scrollContainerRef.current.scrollBy({
        left: direction === 'right' ? scrollAmount : -scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const handleGameClick = async (game) => {
    try {
      // Prepare game object for context
      const gameData = {
        ...game,
        source: useCustomLaunch ? 'jsgames' : 'gis',
        provider: game.provider || (providers && providers[0] ? providers[0].toLowerCase() : 'spribe'),
        vendor: game.vendor || game.provider || (providers && providers[0] ? providers[0].toLowerCase() : 'spribe'),
        uuid: game.uuid || game.id || game.game_uid,
        game_uid: game.game_uid || game.uuid || game.id,
        title: game.name || game.title || game.game_name || 'Game'
      };

      // Use GameContext's handleGameLaunch which handles routing
      await handleGameLaunch(gameData, navigate);
    } catch (error) {
      console.error('Game launch error:', error);
    }
  };

  const handlePagination = (direction) => {
    if (useCustomFetch) {
      // For custom fetch, check if there are more pages
      const hasMorePages = games.length >= itemsPerPage;
      if (direction === 'next' && hasMorePages) {
        setCurrentPage((prev) => prev + 1);
      } else if (direction === 'prev' && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      }
    } else {
      // For GIS providers
      if (direction === 'next' && currentPage < pagination.total_pages) {
        setCurrentPage((prev) => prev + 1);
      } else if (direction === 'prev' && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      }
    }
  };

  const skeletonCount = showMore ? 12 : 8;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        <div className={styles.actions}>
          <button
            className={styles.toggleButton}
            onClick={() => setShowMore(!showMore)}
          >
            {showMore ? t('home.showLess') : t('home.showMore')}
          </button>
          {!showMore && (
            <div className={styles.scrollButtons}>
              <button
                className={styles.scrollButton}
                onClick={() => handleScroll('left')}
                aria-label="Scroll left"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                className={styles.scrollButton}
                onClick={() => handleScroll('right')}
                aria-label="Scroll right"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {isLoading && games.length === 0 ? (
        <div className={showMore ? styles.gridContainer : styles.carouselContainer}>
          {Array.from({ length: skeletonCount }).map((_, idx) => (
            <div key={`skeleton-${idx}`} className={styles.skeletonCard}></div>
          ))}
        </div>
      ) : error ? (
        <div className={styles.errorContainer}>
          <p>{t('home.failedToLoadGames')}</p>
        </div>
      ) : showMore ? (
        <>
          <div className={styles.gridContainer}>
            {games.map((game) => (
              <div
                key={game.uuid || game.id || game.game_uid}
                className={styles.gameCard}
                onClick={() => handleGameClick(game)}
              >
                <div className={styles.gameImageContainer}>
                  <img
                    src={game.game_icon || game.image || '/assets/images/default_card3.webp'}
                    alt={game.name || game.game_name || 'Game'}
                    className={styles.gameImage}
                    loading="lazy"
                    onError={(e) => handleImageError(game.uuid || game.id || game.game_uid, e)}
                  />
                  <div className={styles.playOverlay}>
                    <div className={styles.playButton}>
                      <Play size={24} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {(useCustomFetch ? games.length >= itemsPerPage : pagination.total_pages > 1) && (
            <div className={styles.paginationContainer}>
              <button
                className={styles.paginationButton}
                onClick={() => handlePagination('prev')}
                disabled={currentPage <= 1 || isLoading}
              >
                <ChevronLeft size={16} /> {t('home.previous')}
              </button>
              <span className={styles.paginationInfo}>
                {t('home.page')} {currentPage} {t('home.of')} {useCustomFetch ? (games.length >= itemsPerPage ? currentPage + 1 : currentPage) : pagination.total_pages}
              </span>
              <button
                className={styles.paginationButton}
                onClick={() => handlePagination('next')}
                disabled={(useCustomFetch ? games.length < itemsPerPage : currentPage >= pagination.total_pages) || isLoading}
              >
                {t('home.next')} <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className={styles.carouselContainer}>
          <div
            className={styles.scrollContainer}
            ref={scrollContainerRef}
          >
            {games.map((game) => (
              <div
                key={game.uuid || game.id || game.game_uid}
                className={styles.gameCard}
                onClick={() => handleGameClick(game)}
              >
                <div className={styles.gameImageContainer}>
                  <img
                    src={game.game_icon || game.image || '/assets/images/default_card3.webp'}
                    alt={game.name || game.game_name || 'Game'}
                    className={styles.gameImage}
                    loading="lazy"
                    onError={(e) => handleImageError(game.uuid || game.id || game.game_uid, e)}
                  />
                  <div className={styles.playOverlay}>
                    <div className={styles.playButton}>
                      <Play size={24} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GameCards;
